import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Users, Plus, UserPlus } from 'lucide-react';
import API from '@/api/client';
import { formatDateTime } from '@/utils/helpers';
import { toast } from 'sonner';

const FamilyDependents = () => {
  const [loading, setLoading] = useState(true);
  const [dependents, setDependents] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    relationship: '',
  });

  useEffect(() => {
    fetchDependents();
  }, []);

  const fetchDependents = async () => {
    try {
      setLoading(true);
      const response = await API.get('/customer/dependents');
      setDependents(response.data.dependents);
    } catch (error) {
      console.error('Error fetching dependents:', error);
      toast.error('Failed to load family members');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const response = await API.post('/customer/dependents', {
        ...formData,
        age: parseInt(formData.age),
      });
      toast.success(`Added ${formData.name}! +${response.data.points_earned} points earned!`);
      setShowDialog(false);
      setFormData({ name: '', age: '', relationship: '' });
      fetchDependents();
    } catch (error) {
      console.error('Error adding dependent:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to add family member';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
              alt="Physiomobile Logo"
              className="h-10"
            />
            <h1 className="font-heading text-xl font-bold text-gray-800">Family Care</h1>
          </div>
          <Button onClick={() => window.location.href = '/customer/dashboard'} variant="outline">
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8 max-w-5xl mx-auto">
        <PageHeader
          title="Family Dependents"
          subtitle="Manage family members and share your care journey"
          actions={
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-dependent-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Family Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Family Member</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAdd} className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., John Doe"
                      required
                      data-testid="name-input"
                    />
                  </div>
                  <div>
                    <Label>Age</Label>
                    <Input
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      placeholder="e.g., 65"
                      required
                      data-testid="age-input"
                    />
                  </div>
                  <div>
                    <Label>Relationship</Label>
                    <Input
                      value={formData.relationship}
                      onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                      placeholder="e.g., Father, Mother, Child"
                      required
                      data-testid="relationship-input"
                    />
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Earn 200 points</strong> when you add a family member!
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      (Max 2 dependents per month)
                    </p>
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-dependent-button">
                    Add Family Member
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          }
        />

        {loading ? (
          <LoadingSpinner size="lg" text="Loading family members..." />
        ) : (
          <div>
            {dependents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dependents.map((dependent, index) => (
                  <Card
                    key={dependent.id}
                    className="p-6 hover:shadow-lg transition-shadow"
                    data-testid={`dependent-card-${index}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <UserPlus className="w-6 h-6 text-primary" />
                      </div>
                    </div>

                    <h3 className="font-heading text-xl font-semibold mb-1 text-gray-800">
                      {dependent.name}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-3">
                      {dependent.relationship} • Age {dependent.age}
                    </p>

                    <div className="text-xs text-muted-foreground">
                      Added {formatDateTime(dependent.created_at)}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold text-gray-800 mb-2">
                  No family members yet
                </h3>
                <p className="text-muted-foreground mb-4">
                  Add your family members to share your care journey and earn bonus points
                </p>
                <Button onClick={() => setShowDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Family Member
                </Button>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default FamilyDependents;